import React, { useState, useEffect, useContext } from 'react';
import styled from 'styled-components';
import { MailingContext } from '../../../../contexts/MailingContext';
import { useFormFields } from '../../../../contexts/FormFieldsContext';
import { showSuccessMessage, showErrorMessage } from '../../../../helpers/useToast';

const InventoryModal = ({ show, onHide, editItem = null, defaultType = 'physical' }) => {
  const { createInventory, updateInventory } = useContext(MailingContext);
  const { formFields, loadingFormFields } = useFormFields();
  const callTypes = formFields.inventoryCallTypes;
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    type: 'physical',
    subclass: '',
    location: '',
    url: '',
    quantityAvailable: 0,
    quantityOnHold: 0,
    distributor: 'internal',
    activeStatus: 'active'
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (editItem) {
      setFormData({
        title: editItem.title || '',
        description: editItem.description || '',
        type: editItem.type || 'physical',
        subclass: editItem.subclass || '',
        location: editItem.location || '',
        url: editItem.url || '',
        quantityAvailable: editItem.quantityAvailable || 0,
        quantityOnHold: editItem.quantityOnHold || 0,
        distributor: editItem.distributor || 'internal',
        activeStatus: editItem.activeStatus || 'active'
      });
    } else {
      setFormData({
        title: '',
        description: '',
        type: defaultType,
        subclass: '',
        location: '',
        url: '',
        quantityAvailable: 0,
        quantityOnHold: 0,
        distributor: 'internal',
        activeStatus: 'active'
      });
    }
  }, [editItem]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const getSelectedSubclassIds = (subclass) => {
    if (!subclass) return [];
    return subclass.split(';').map((id) => id.trim()).filter(Boolean);
  };

  const handleSubclassToggle = (id) => {
    const idStr = String(id);
    const selectedIds = getSelectedSubclassIds(formData.subclass);
    const updatedIds = selectedIds.includes(idStr)
      ? selectedIds.filter((selectedId) => selectedId !== idStr)
      : [...selectedIds, idStr];

    setFormData((prev) => ({
      ...prev,
      subclass: updatedIds.join(';'),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (editItem) {
        // Update existing inventory
        await updateInventory(editItem.inventoryID, formData);
        showSuccessMessage('Inventory updated successfully!');
      } else {
        // Create new inventory
        await createInventory(formData);
        showSuccessMessage('Inventory created successfully!');
      }
      onHide();
    } catch (error) {
      console.error('Error saving inventory:', error);
      showErrorMessage('Failed to save inventory. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  const selectedSubclassIds = getSelectedSubclassIds(formData.subclass);

  return (
    <ModalOverlay>
      <ModalContent>
        <ModalHeader>
          <ModalTitle>{editItem ? 'Edit Inventory' : 'Add New Inventory'}</ModalTitle>
          <CloseButton onClick={onHide}>&times;</CloseButton>
        </ModalHeader>
        
        <ModalBody>
          <Form onSubmit={handleSubmit}>
            <FormGroup>
              <Label>Title *</Label>
              <Input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                required
                placeholder="Enter inventory title"
              />
            </FormGroup>

            <FormGroup>
              <Label>Description</Label>
              <TextArea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Enter description"
                rows="3"
              />
            </FormGroup>

            <FormRow>
              <FormGroup>
                <Label>Type</Label>
                <Select
                  name="type"
                  value={formData.type}
                  onChange={handleInputChange}
                >
                  <option value="physical">Physical</option>
                  <option value="digital">Digital</option>
                </Select>
              </FormGroup>
            </FormRow>

            <FormGroup>
              <Label>Subclass (Call Types)</Label>
              <CheckboxGroup>
                {loadingFormFields ? (
                  <LoadingText>Loading call types...</LoadingText>
                ) : callTypes.length === 0 ? (
                  <LoadingText>No call types available.</LoadingText>
                ) : (
                  callTypes.map((callType) => (
                    <CallTypeGroup key={callType.id}>
                      <CheckboxItem>
                        <CheckboxInput
                          type="checkbox"
                          id={`subclass-${callType.id}`}
                          checked={selectedSubclassIds.includes(String(callType.id))}
                          onChange={() => handleSubclassToggle(callType.id)}
                        />
                        <CheckboxLabel htmlFor={`subclass-${callType.id}`}>
                          {callType.label}
                        </CheckboxLabel>
                      </CheckboxItem>

                      {callType.children.length > 0 && (
                        <ChildCheckboxList>
                          {callType.children.map((child) => (
                            <CheckboxItem key={child.id}>
                              <CheckboxInput
                                type="checkbox"
                                id={`subclass-${child.id}`}
                                checked={selectedSubclassIds.includes(String(child.id))}
                                onChange={() => handleSubclassToggle(child.id)}
                              />
                              <CheckboxLabel htmlFor={`subclass-${child.id}`}>
                                {child.label}
                              </CheckboxLabel>
                            </CheckboxItem>
                          ))}
                        </ChildCheckboxList>
                      )}
                    </CallTypeGroup>
                  ))
                )}
              </CheckboxGroup>
            </FormGroup>

            <FormRow>
              <FormGroup>
                <Label>Location</Label>
                <Input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  placeholder="Enter location"
                />
              </FormGroup>

              {formData.type === 'digital' && (
                <FormGroup>
                  <Label>URL</Label>
                  <Input
                    type="url"
                    name="url"
                    value={formData.url}
                    onChange={handleInputChange}
                    placeholder="Enter URL for digital resource"
                  />
                </FormGroup>
              )}
            </FormRow>

            <FormRow>
              <FormGroup>
                <Label>Quantity Available</Label>
                <Input
                  type="number"
                  name="quantityAvailable"
                  value={formData.quantityAvailable}
                  onChange={handleInputChange}
                  min="0"
                  placeholder="Enter available quantity"
                />
              </FormGroup>

              {editItem && (
                <FormGroup>
                  <Label>Quantity On Hold (System)</Label>
                  <Input
                    type="number"
                    name="quantityOnHold"
                    value={formData.quantityOnHold}
                    disabled
                    placeholder="System managed"
                  />
                </FormGroup>
              )}
            </FormRow>

            <FormRow>
              <FormGroup>
                <Label>Dispatcher</Label>
                <Select
                  name="distributor"
                  value={formData.distributor}
                  onChange={handleInputChange}
                >
                  <option value="internal">Internal</option>
                  <option value="Anchor">Anchor</option>
                </Select>
              </FormGroup>

              <FormGroup>
                <Label>Active Status</Label>
                <Select
                  name="activeStatus"
                  value={formData.activeStatus}
                  onChange={handleInputChange}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </Select>
              </FormGroup>
            </FormRow>

            <ButtonGroup>
              <CancelButton type="button" onClick={onHide}>
                Cancel
              </CancelButton>
              <SubmitButton type="submit" disabled={loading}>
                {loading ? 'Saving...' : (editItem ? 'Update' : 'Create')}
              </SubmitButton>
            </ButtonGroup>
          </Form>
        </ModalBody>
      </ModalContent>
    </ModalOverlay>
  );
};

export default InventoryModal;

// Styled Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #e5e7eb;
`;

const ModalTitle = styled.h2`
  margin: 0;
  color: #1f2937;
  font-size: 1.25rem;
  font-weight: 600;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6b7280;
  
  &:hover {
    color: #374151;
  }
`;

const ModalBody = styled.div`
  padding: 20px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const Label = styled.label`
  font-weight: 500;
  color: #374151;
  font-size: 0.875rem;
`;

const Input = styled.input`
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 0.875rem;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const TextArea = styled.textarea`
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 0.875rem;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const Select = styled.select`
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 0.875rem;
  background-color: white;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const CheckboxGroup = styled.div`
  max-height: 240px;
  overflow-y: auto;
  padding: 12px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  background-color: #fafafa;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const CallTypeGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const ChildCheckboxList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-left: 20px;
  padding-left: 12px;
  border-left: 2px solid #e5e7eb;
`;

const CheckboxItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
`;

const CheckboxInput = styled.input`
  margin-top: 2px;
  width: 16px;
  height: 16px;
  min-width: 16px;
  min-height: 16px;
  flex-shrink: 0;
  cursor: pointer;
  appearance: auto;
  -webkit-appearance: checkbox;
  accent-color: #3b82f6;
  opacity: 1;
  visibility: visible;
`;

const CheckboxLabel = styled.label`
  font-size: 0.875rem;
  color: #374151;
  cursor: pointer;
  line-height: 1.4;
`;

const LoadingText = styled.span`
  font-size: 0.875rem;
  color: #6b7280;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 20px;
`;

const Button = styled.button`
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
`;

const CancelButton = styled(Button)`
  background-color: #f3f4f6;
  color: #374151;
  border: 1px solid #d1d5db;
  
  &:hover {
    background-color: #e5e7eb;
  }
`;

const SubmitButton = styled(Button)`
  background-color: #3b82f6;
  color: white;
  border: 1px solid #3b82f6;
  
  &:hover:not(:disabled) {
    background-color: #2563eb;
  }
  
  &:disabled {
    background-color: #9ca3af;
    cursor: not-allowed;
  }
`; 